import { useEffect, useMemo, useRef, useState } from "react";
import { useAuth } from "@/context/AuthContext";
import { atLeast } from "@/lib/roles";
import {
  biMonthly, biTrend, biMoM, biWeekly, reportKPIs,
  getLeadsCount, listCustomers,
  biTrendFilters, biTrendFiltered, listRenewals, listSoldNotStarted,
  dashboardHeadlineKpis, getBloodTests,
} from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Link } from "react-router-dom";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { toast } from "sonner";
import {
  LineChart, Line, ResponsiveContainer, CartesianGrid, XAxis, YAxis, Tooltip,
  PieChart, Pie, Cell, BarChart, Bar,
} from "recharts";
import {
  ChevronLeft, ChevronRight, IndianRupee, TrendingUp, ReceiptText, Users, Target,
  Percent, Wallet, ArrowUp, ArrowDown, Minus, RefreshCw, BellRing, Zap,
  FileDown, X, Timer,
} from "lucide-react";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import jsPDF from "jspdf";
import html2canvas from "html2canvas";
import { numberLocale } from "@/lib/money";
import { BirthdayBanner } from "@/components/StaffBirthday";

const fmtINR = (n) => n == null ? "—" : `${window.__ACTIVE_CURRENCY_SYMBOL__ || ""}${new Intl.NumberFormat(numberLocale(), { maximumFractionDigits: 0 }).format(n)}`;
const fmtN = (n) => new Intl.NumberFormat(numberLocale()).format(n || 0);
const pct = (n) => n == null ? "—" : `${Math.round((n || 0) * 1000) / 10}%`;
const MONTHS = ["Jan","Feb","Mar","Apr","May","Jun","Jul","Aug","Sep","Oct","Nov","Dec"];
const PIE_COLORS = ["#60a5fa", "#34d399", "#f59e0b", "#f43f5e", "#a78bfa", "#22d3ee", "#fb7185", "#fbbf24"];

export default function Dashboard() {
  const { user, activeFacility } = useAuth();
  const today = new Date();
  const [year, setYear] = useState(today.getFullYear());
  const [month, setMonth] = useState(today.getMonth() + 1);
  const [selectedWeek, setSelectedWeek] = useState("all");

  const [bi, setBI] = useState(null);
  const [mom, setMoM] = useState(null);
  const [kpis, setKPIs] = useState(null);
  const [weekly, setWeekly] = useState(null);
  const [trend, setTrend] = useState([]);
  const [customers, setCustomers] = useState([]);
  const [renewals, setRenewals] = useState({ window_days: 10, items: [] });

  const [leadsInput, setLeadsInput] = useState("");
  const [busy, setBusy] = useState(false);
  const [exporting, setExporting] = useState(false);
  const exportRef = useRef(null);

  // Trend chart filters
  const [trendReps, setTrendReps] = useState([]);
  const [trendProgrammes, setTrendProgrammes] = useState([]);
  const [trendRep, setTrendRep] = useState("");
  const [trendProgramme, setTrendProgramme] = useState("");

  const showRepLeaderboard = atLeast(user?.role, "admin");

  const load = async () => {
    setBusy(true);
    try {
      const [b, m, k, w, t, c, lc, ren] = await Promise.all([
        biMonthly(year, month), biMoM(year, month), reportKPIs(year, month),
        biWeekly(year, month), biTrend(), listCustomers(),
        getLeadsCount(year, month), listRenewals(),
      ]);
      setBI(b); setMoM(m); setKPIs(k); setWeekly(w); setTrend(t.points || []); setCustomers(c);
      setLeadsInput(String(lc.leads_count || 0));
      setRenewals(ren);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Failed to load dashboard");
    } finally {
      setBusy(false);
    }
  };
  useEffect(() => { load(); setSelectedWeek("all"); }, [year, month]);

  // Load trend filter options once
  useEffect(() => {
    biTrendFilters().then((d) => {
      setTrendReps(d.reps || []);
      setTrendProgrammes(d.programmes || []);
    }).catch(() => {});
  }, []);

  // Re-fetch just the trend chart when rep/programme filters change
  useEffect(() => {
    biTrendFiltered(trendRep, trendProgramme).then((t) => setTrend(t.points || []));
  }, [trendRep, trendProgramme]);

  const exportPDF = async () => {
    if (!exportRef.current) return;
    setExporting(true);
    try {
      // Give the DOM a beat to render any freshly-opened section
      await new Promise((r) => setTimeout(r, 100));
      const canvas = await html2canvas(exportRef.current, {
        backgroundColor: "#0a0a0a",
        scale: 1.5,
        useCORS: true,
        windowWidth: exportRef.current.scrollWidth,
      });
      const imgData = canvas.toDataURL("image/jpeg", 0.9);
      const pdf = new jsPDF({ orientation: "portrait", unit: "px", format: "a4" });
      const pageW = pdf.internal.pageSize.getWidth();
      const pageH = pdf.internal.pageSize.getHeight();
      const imgW = pageW;
      const imgH = (canvas.height * imgW) / canvas.width;
      let heightLeft = imgH;
      let position = 0;
      pdf.addImage(imgData, "JPEG", 0, position, imgW, imgH);
      heightLeft -= pageH;
      while (heightLeft > 0) {
        position = heightLeft - imgH;
        pdf.addPage();
        pdf.addImage(imgData, "JPEG", 0, position, imgW, imgH);
        heightLeft -= pageH;
      }
      const fname = `FullPowerAnalytics_${MONTHS[month-1]}-${year}.pdf`;
      pdf.save(fname);
      toast.success(`Exported ${fname}`);
    } catch (err) {
      console.error("PDF export failed:", err);
      toast.error("PDF export failed — try again");
    } finally {
      setExporting(false);
    }
  };

  const shift = (delta) => {
    let m = month + delta, y = year;
    if (m < 1) { m = 12; y -= 1; }
    if (m > 12) { m = 1; y += 1; }
    setMonth(m); setYear(y);
  };

  // Scope numbers to selected week if user picked one
  const scopedKPIs = useMemo(() => {
    if (!kpis) return null;
    if (selectedWeek === "all" || !weekly) return kpis;
    const w = (weekly.weeks || []).find((x) => x.week === selectedWeek);
    if (!w) return kpis;
    // Per-week versions of leads & CPL & CAC using the week's leads counts (Feb 2026)
    const leadsWeek = w.leads_total || 0;
    const roas = kpis.expenses.ad_spend > 0 ? Math.round((w.revenue / kpis.expenses.ad_spend) * 100) / 100 : null;
    // Estimate ad spend for the week — proportional to weeks in month if not tracked weekly
    // For now we use the FULL monthly ad_spend but flag it as "monthly" in the UI
    const cpl = kpis.expenses.ad_spend > 0 && leadsWeek > 0 ? Math.round(kpis.expenses.ad_spend / leadsWeek) : null;
    return {
      ...kpis,
      revenue: { ...kpis.revenue, total_revenue: w.revenue, total_paid: w.paid, total_profit: w.profit },
      expenses: { ...kpis.expenses, total_expenses: w.expenses },
      acquisition: {
        ...kpis.acquisition,
        sales_count: w.sales_count,
        leads_count: leadsWeek,
        cost_per_lead: cpl,
        roas,
        all_in_cost_per_sale: w.sales_count > 0 ? Math.round(w.expenses / w.sales_count) : null,
      },
    };
  }, [kpis, selectedWeek, weekly]);

  if (!scopedKPIs || !bi) return <div className="text-sm text-zinc-500">Loading…</div>;

  const renewalDue = renewals?.items?.length ?? customers.filter((c) => c.renewal_due).length;
  const scopeLabel = selectedWeek === "all" ? `${MONTHS[month-1]} ${year}` : `Week ${selectedWeek} · ${MONTHS[month-1]} ${year}`;
  const salesMix = [
    { name: "FE", value: bi.sales.fe },
    { name: "PIF", value: bi.sales.pif },
    { name: "PT", value: bi.sales.pt },
    { name: "SPT", value: bi.sales.spt },
  ].filter((s) => s.value > 0);
  const progMix = Object.entries(bi.sales.programme_mix || {}).map(([name, value]) => ({ name, value }));
  const statusMix = Object.entries(bi.sales.status_mix || {}).map(([name, value]) => ({ name, value }));

  return (
    <div className="space-y-10" data-testid="dashboard-page">
      <BirthdayBanner facilityId={activeFacility?.id} />
      {/* Header + filters */}
      <section className="flex items-end justify-between flex-wrap gap-3">
        <div>
          <div className="eyebrow">Gym business overview</div>
          <h2 className="font-display text-3xl font-extrabold mt-1 tracking-tight" data-testid="dashboard-scope-title">
            {scopeLabel}
          </h2>
          <p className="text-sm text-zinc-400 mt-1">
            Revenue, expenses, payroll, acquisition, sales mix and month-on-month deltas — all in one place.
          </p>
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          <div className="flex items-center gap-1 card-surface p-1 rounded-full">
            <Button variant="ghost" size="sm" onClick={() => shift(-1)} className="rounded-full h-8 w-8 p-0" data-testid="dashboard-prev-month">
              <ChevronLeft className="h-4 w-4" />
            </Button>
            <div className="px-3 text-sm font-medium tabular-nums" data-testid="dashboard-current-period">
              {MONTHS[month-1]} {year}
            </div>
            <Button variant="ghost" size="sm" onClick={() => shift(1)} className="rounded-full h-8 w-8 p-0" data-testid="dashboard-next-month">
              <ChevronRight className="h-4 w-4" />
            </Button>
          </div>
          <Button onClick={load} disabled={busy} variant="outline" className="border-[#262626] bg-transparent hover:bg-[#141414]" data-testid="dashboard-refresh">
            <RefreshCw className={`h-3.5 w-3.5 mr-2 ${busy ? "animate-spin" : ""}`} /> Refresh
          </Button>
          <Button
            onClick={exportPDF}
            disabled={exporting}
            className="bg-rose-600 hover:bg-rose-500 rounded-full px-5"
            data-testid="dashboard-export-pdf"
          >
            <FileDown className={`h-3.5 w-3.5 mr-2 ${exporting ? "animate-pulse" : ""}`} />
            {exporting ? "Building PDF…" : "Export PDF"}
          </Button>
        </div>
      </section>

      <div ref={exportRef} className="space-y-10">
        {/* PDF export cover — only visible in the PDF; hidden in the app */}
        <div className="hidden print:block pdf-cover">
          <h1 className="font-display text-3xl sm:text-4xl font-extrabold">{scopeLabel}</h1>
          <div className="eyebrow mt-2">FullPowerAnalytics by LIFTR</div>
        </div>

      {/* Week filter */}
      {weekly?.weeks?.length > 0 && (
        <section data-testid="dashboard-week-filter">
          <div className="eyebrow mb-2">Filter by week</div>
          <div className="flex gap-2 flex-wrap">
            <WeekChip data-testid="dashboard-week-chip-246" active={selectedWeek === "all"} onClick={() => setSelectedWeek("all")} label="Whole month" testid="week-chip-all" />
            {(weekly.weeks || []).map((w) => (
              <WeekChip data-testid="dashboard-week-chip-248"
                key={w.week}
                active={selectedWeek === w.week}
                onClick={() => setSelectedWeek(w.week)}
                label={`W${w.week} · ${new Date(w.start).toLocaleDateString(undefined, { day: "numeric", month: "short" })}`}
                sub={`${window.__ACTIVE_CURRENCY_SYMBOL__ || ""}${fmtN(w.revenue)} · ${w.sales_count} sales`}
                testid={`week-chip-${w.week}`}
              />
            ))}
          </div>
        </section>
      )}

      {/* Life Time Value — hero metric, moved to top */}
      <section data-testid="ltv-section">
        <div className="eyebrow mb-3">Life Time Value · trailing 365 days · your north-star metric</div>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          <BigKpi
            label="LTV"
            value={fmtINR(kpis.ltv?.ltv)}
            sub={kpis.ltv?.ltv_formula}
            icon={TrendingUp}
            tint="emerald"
            testid="kpi-ltv"
          />
          <BigKpi
            label="LTV : CAC"
            value={kpis.ltv?.ltv_cac_ratio != null ? `${kpis.ltv.ltv_cac_ratio}x` : "—"}
            sub={kpis.ltv?.ltv_cac_ratio >= 3 ? "healthy (≥ 3×)" : "under 3× — investigate"}
            icon={Target}
            tint={kpis.ltv?.ltv_cac_ratio >= 3 ? "emerald" : "rose"}
            testid="kpi-ltv-cac-ratio"
          />
          <BigKpi
            label="Avg member lifespan"
            value={kpis.ltv?.acl != null ? `${kpis.ltv.acl} yr` : "—"}
            sub="1 ÷ churn rate"
            icon={Users}
            tint="blue"
            testid="kpi-acl"
          />
          <BigKpi
            label="Purchase frequency"
            value={kpis.ltv?.purchase_frequency != null ? kpis.ltv.purchase_frequency.toFixed(6) : "—"}
            sub={kpis.ltv?.purchase_frequency_note}
            icon={Percent}
            tint="violet"
            testid="kpi-purchase-frequency"
          />
        </div>
      </section>

      {/* Recording coverage moved to Monthly Sales Dashboard */}
      <HeadlineKpiStrip year={year} month={month} />


      {/* Money — headline */}
      <section>
        <div className="eyebrow mb-3">Money · in and out {selectedWeek !== "all" ? "(scoped to selected week)" : "· vs previous month"}</div>
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
          <BigKpi
            label="Revenue"
            value={fmtINR(scopedKPIs.revenue.total_revenue)}
            sub={selectedWeek === "all" ? `Paid ${fmtINR(scopedKPIs.revenue.total_paid)}` : ""}
            icon={IndianRupee}
            tint="emerald"
            delta={selectedWeek === "all" ? mom?.delta?.revenue : null}
            testid="kpi-revenue"
          />
          <BigKpi
            label="Expenses"
            value={fmtINR(scopedKPIs.expenses.total_expenses)}
            sub={`Ad spend ${fmtINR(scopedKPIs.expenses.ad_spend)}`}
            icon={ReceiptText}
            tint="rose"
            delta={selectedWeek === "all" ? mom?.delta?.expenses : null}
            invertDelta
            testid="kpi-expenses"
          />
          <BigKpi
            label="Profit"
            value={fmtINR(scopedKPIs.revenue.total_profit)}
            sub="Revenue − expenses"
            icon={scopedKPIs.revenue.total_profit >= 0 ? TrendingUp : ArrowDown}
            tint={scopedKPIs.revenue.total_profit >= 0 ? "emerald" : "rose"}
            delta={selectedWeek === "all" ? mom?.delta?.profit : null}
            testid="kpi-profit"
          />
          <BigKpi
            label="Sales"
            value={fmtN(scopedKPIs.acquisition.sales_count)}
            sub={`${scopedKPIs.acquisition.new_customers} new members`}
            icon={Users}
            tint="blue"
            delta={selectedWeek === "all" ? mom?.delta?.sales_count : null}
            format="num"
            testid="kpi-sales"
          />
        </div>
      </section>

      {/* 12-month trend */}
      <section className="card-surface p-4 sm:p-6" data-testid="trend-chart">
        <div className="flex items-baseline justify-between mb-1 flex-wrap gap-3">
          <div>
            <div className="eyebrow">Revenue trend · last 12 months</div>
            <h3 className="font-display text-xl font-bold mt-1">The shape of your year</h3>
          </div>
          <div className="flex items-center gap-2 flex-wrap">
            {(trendReps.length > 0 || trendProgrammes.length > 0) && (
              <>
                <Select data-testid="dashboard-select-359" value={trendRep || "all"} onValueChange={(v) => setTrendRep(v === "all" ? "" : v)}>
                  <SelectTrigger className="bg-[#0a0a0a] border-[#262626] w-40 h-8 text-xs" data-testid="trend-rep-filter">
                    <SelectValue placeholder="All reps" />
                  </SelectTrigger>
                  <SelectContent className="bg-[#0f0f0f] border-[#262626] text-zinc-100">
                    <SelectItem data-testid="dashboard-select-item-364" value="all" className="focus:bg-[#1a1a1a]">All reps</SelectItem>
                    {trendReps.map((r) => (
                      <SelectItem data-testid="dashboard-select-item-366" key={r} value={r} className="focus:bg-[#1a1a1a]">{r}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                <Select data-testid="dashboard-select-370" value={trendProgramme || "all"} onValueChange={(v) => setTrendProgramme(v === "all" ? "" : v)}>
                  <SelectTrigger className="bg-[#0a0a0a] border-[#262626] w-40 h-8 text-xs" data-testid="trend-programme-filter">
                    <SelectValue placeholder="All programmes" />
                  </SelectTrigger>
                  <SelectContent className="bg-[#0f0f0f] border-[#262626] text-zinc-100">
                    <SelectItem data-testid="dashboard-select-item-375" value="all" className="focus:bg-[#1a1a1a]">All programmes</SelectItem>
                    {trendProgrammes.map((p) => (
                      <SelectItem data-testid="dashboard-select-item-377" key={p} value={p} className="focus:bg-[#1a1a1a]">{p}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                {(trendRep || trendProgramme) && (
                  <button
                    onClick={() => { setTrendRep(""); setTrendProgramme(""); }}
                    className="text-xs text-zinc-500 hover:text-white inline-flex items-center gap-1"
                    data-testid="trend-clear-filters"
                  >
                    <X className="h-3 w-3" /> Clear
                  </button>
                )}
              </>
            )}
            <div className="flex items-center gap-3 text-[11px] uppercase tracking-[0.18em] text-zinc-500 ml-1">
              <span className="flex items-center gap-1.5"><span className="h-2 w-2 rounded-full bg-blue-400" />Paid</span>
              <span className="flex items-center gap-1.5"><span className="h-2 w-2 rounded-full bg-emerald-400" />Net</span>
            </div>
          </div>
        </div>
        <div className="h-64 mt-4">
          {trend.length === 0 ? (
            <div className="h-full flex items-center justify-center text-sm text-zinc-500">Once sales are logged, the trend appears here.</div>
          ) : (
            <ResponsiveContainer width="100%" height="100%">
              <LineChart data={trend} margin={{ top: 10, right: 12, left: -6, bottom: 0 }}>
                <CartesianGrid stroke="#1f1f1f" vertical={false} />
                <XAxis dataKey="label" stroke="#52525b" fontSize={11} tickLine={false} axisLine={{ stroke: "#1f1f1f" }} />
                <YAxis stroke="#52525b" fontSize={11} tickLine={false} axisLine={false} tickFormatter={(v) => v >= 100000 ? `${(v/100000).toFixed(1)}L` : v >= 1000 ? `${v/1000}K` : v} />
                <Tooltip contentStyle={{ background: "#0f0f0f", border: "1px solid #262626", borderRadius: 8, fontSize: 12 }} formatter={(v) => fmtINR(v)} />
                <Line type="monotone" dataKey="paid" stroke="#60a5fa" strokeWidth={2} dot={{ r: 3, fill: "#60a5fa" }} />
                <Line type="monotone" dataKey="net" stroke="#34d399" strokeWidth={2} dot={{ r: 3, fill: "#34d399" }} />
              </LineChart>
            </ResponsiveContainer>
          )}
        </div>
      </section>

      {/* Weekly breakdown */}
      {selectedWeek === "all" && weekly?.weeks?.length > 0 && (
        <section className="card-surface p-4 sm:p-6" data-testid="weekly-breakdown">
          <div className="eyebrow mb-3">Weekly breakdown</div>
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="text-left text-[11px] uppercase tracking-[0.2em] text-zinc-500 border-b border-[#1f1f1f]">
                  <th className="px-3 py-2 font-medium">Week</th>
                  <th className="px-3 py-2 font-medium">Dates</th>
                  <th className="px-3 py-2 font-medium text-right">Sales #</th>
                  <th className="px-3 py-2 font-medium text-right">Revenue</th>
                  <th className="px-3 py-2 font-medium text-right">Expenses</th>
                  <th className="px-3 py-2 font-medium text-right">Profit</th>
                </tr>
              </thead>
              <tbody>
                {(weekly.weeks || []).map((w) => (
                  <tr data-testid="dashboard-tr-434" key={w.week} className="border-b border-[#1a1a1a] last:border-0 hover:bg-[#161616] cursor-pointer" onClick={() => setSelectedWeek(w.week)}>
                    <td className="px-3 py-2.5 font-medium">W{w.week}</td>
                    <td className="px-3 py-2.5 text-zinc-400 text-xs">{new Date(w.start).toLocaleDateString()} → {new Date(w.end).toLocaleDateString()}</td>
                    <td className="px-3 py-2.5 text-right">{w.sales_count}</td>
                    <td className="px-3 py-2.5 text-right font-mono text-emerald-300">{fmtINR(w.revenue)}</td>
                    <td className="px-3 py-2.5 text-right font-mono text-rose-300">{fmtINR(w.expenses)}</td>
                    <td className={`px-3 py-2.5 text-right font-mono font-semibold ${w.profit >= 0 ? "text-emerald-400" : "text-rose-400"}`}>{fmtINR(w.profit)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </section>
      )}

      {/* Acquisition efficiency */}
      <section>
        <div className="flex items-end justify-between mb-3">
          <div className="flex items-center gap-2">
            <div className="eyebrow">Acquisition efficiency</div>
            {selectedWeek !== "all" && (
              <span className="text-[10px] uppercase tracking-[0.18em] text-amber-400/80 border border-amber-500/30 bg-amber-500/5 rounded-full px-2 py-0.5" data-testid="monthly-scope-note">
                Some metrics stay monthly
              </span>
            )}
          </div>
          <Link to="/leads" className="text-xs text-zinc-500 hover:text-zinc-300" data-testid="leads-count-auto">
            Leads this month: <span className="font-mono text-zinc-200">{leadsInput || 0}</span> <span className="text-zinc-600">· counted automatically</span>
          </Link>
        </div>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          <Kpi label="Leads" value={fmtN(scopedKPIs.acquisition.leads_count)} icon={Users} tint="blue" testid="kpi-leads" />
          <Kpi label="Ad spend" value={fmtINR(kpis.expenses.ad_spend)} icon={Target} tint="rose" scopeBadge={selectedWeek !== "all" ? "month" : null} testid="kpi-ad-spend" />
          <Kpi label="ROAS" value={scopedKPIs.acquisition.roas != null ? `${scopedKPIs.acquisition.roas}x` : "—"} icon={TrendingUp} tint="emerald" sub="Revenue ÷ Ad spend" testid="kpi-roas" />
          <Kpi
            label="Cost per lead"
            value={fmtINR(scopedKPIs.acquisition.cost_per_lead)}
            icon={IndianRupee}
            sub={scopedKPIs.acquisition.leads_count > 0 ? `Ad spend ÷ ${scopedKPIs.acquisition.leads_count} leads` : "Set leads count →"}
            testid="kpi-cpl"
          />
          <Kpi
            label="Cost per acquisition"
            value={fmtINR(kpis.acquisition.cost_per_acquisition)}
            icon={Users}
            sub={`(Ad spend ${fmtINR(kpis.expenses.ad_spend)} + Incentives ${fmtINR(kpis.payroll.incentive_payouts)}) ÷ ${kpis.acquisition.new_customers} new members`}
            scopeBadge={selectedWeek !== "all" ? "month" : null}
            testid="kpi-cac"
          />
          <Kpi label="All-in cost / sale" value={fmtINR(scopedKPIs.acquisition.all_in_cost_per_sale)} icon={ReceiptText} sub={`Expenses ÷ ${scopedKPIs.acquisition.sales_count} sales`} testid="kpi-cost-per-sale" />
          <Kpi label="New members" value={fmtN(kpis.acquisition.new_customers)} icon={Users} tint="blue" scopeBadge={selectedWeek !== "all" ? "month" : null} testid="kpi-new-customers" />
          <Kpi label="Renewals due" value={fmtN(renewalDue)} icon={BellRing} tint={renewalDue > 0 ? "amber" : "emerald"} sub={`Within ${renewals?.window_days || 10} days`} testid="kpi-renewals-due" />
        </div>
      </section>

      {/* Payroll */}
      <section>
        <div className="eyebrow mb-3">Payroll</div>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          <Kpi label="Payroll cost" value={fmtINR(kpis.payroll.payroll_cost)} icon={Wallet} tint="violet" testid="kpi-payroll-cost" />
          <Kpi label="Salaries" value={fmtINR(kpis.payroll.salaries)} icon={Wallet} testid="kpi-salaries" />
          <Kpi label="Incentive payouts" value={fmtINR(kpis.payroll.incentive_payouts)} icon={Percent} testid="kpi-incentive-payouts" />
          <Kpi label="Payroll / revenue" value={pct(kpis.payroll.payroll_pct_of_revenue)} icon={Percent} tint={kpis.payroll.payroll_pct_of_revenue > 0.4 ? "rose" : "emerald"} testid="kpi-payroll-pct" />
        </div>
      </section>

      {/* Sold — programme not started (pending incentives) */}
      <SoldNotStartedCard />

      {/* Members & revenue split */}
      <section data-testid="members-section">
        <div className="eyebrow mb-3">Members & extras</div>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          <BigKpi
            label="Active members"
            value={fmtN(kpis.members?.active_members_end_of_month)}
            sub={`As of ${new Date(Math.min(new Date(kpis.period_end), Date.now())).toLocaleDateString()}`}
            icon={Users}
            tint="emerald"
            testid="kpi-active-members"
          />
          <Kpi label="Revenue / active member" value={fmtINR(kpis.revenue?.revenue_per_active_member)} icon={IndianRupee} tint="blue" testid="kpi-rev-per-member" />
          <Kpi label="Membership revenue" value={fmtINR(kpis.revenue?.membership_revenue)} icon={IndianRupee} tint="emerald" sub="Programmes & packages" testid="kpi-membership-revenue" />
          <Kpi
            label="Other revenue"
            value={fmtINR(kpis.revenue?.other_revenue)}
            icon={IndianRupee}
            tint="amber"
            sub={[["special_programme", "Special"], ["service", "Services"], ["merch", "Merch"], ["community", "Community"]]
              .map(([k, label]) => `${label} ${fmtINR(kpis.revenue?.revenue_by_category?.[k] || 0)}`).join(" · ")}
            testid="kpi-other-revenue"
          />
        </div>
      </section>

      {/* Life Time Value section moved to top (hero metric) */}

      {/* Income breakdown */}
      <section>
        <div className="eyebrow mb-2">Income breakdown (full month)</div>
        <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
          <Kpi label="Total paid" value={fmtINR(bi.income.total_paid)} icon={IndianRupee} tint="blue" testid="kpi-total-paid" />
          <Kpi label="GST collected" value={fmtINR(bi.income.gst)} icon={IndianRupee} tint="amber" />
          <Kpi label="Platform fee" value={fmtINR(bi.income.platform_fee)} icon={Zap} tint="rose" />
          <Kpi label="Net revenue" value={fmtINR(bi.income.net_revenue)} icon={TrendingUp} tint="emerald" />
          <Kpi label="Pending" value={fmtINR(bi.income.pending_payment)} icon={Target} tint="violet" />
        </div>
      </section>

      {/* Sales report */}
      <section>
        <div className="eyebrow mb-2">Sales report (full month)</div>
        <div className="grid grid-cols-2 md:grid-cols-6 gap-4">
          <Kpi label="Sales #" value={fmtN(bi.sales.count)} icon={Users} tint="blue" />
          <Kpi label="Front End" value={fmtN(bi.sales.fe)} icon={Target} />
          <Kpi label="PIFs" value={fmtN(bi.sales.pif)} icon={Target} />
          <Kpi label="PT" value={fmtN(bi.sales.pt)} icon={Target} />
          <Kpi label="Avg ticket" value={fmtINR(bi.sales.avg_ticket)} icon={IndianRupee} tint="emerald" />
          <Kpi label="Up front cash" value={fmtINR(bi.sales.up_front_cash)} icon={IndianRupee} tint="amber" />
        </div>
      </section>

      {/* Sales mix + programme mix side-by-side */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <ChartCard title="Sales type mix">
          {salesMix.length === 0 ? <Empty /> : (
            <ResponsiveContainer width="100%" height={220}>
              <PieChart>
                <Pie data={salesMix} dataKey="value" nameKey="name" outerRadius={80} innerRadius={45}>
                  {(salesMix || []).map((entry, i) => <Cell key={entry.name || `slice-${i}`} fill={PIE_COLORS[i % PIE_COLORS.length]} />)}
                </Pie>
                <Tooltip contentStyle={{ background: "#0f0f0f", border: "1px solid #262626", borderRadius: 8, fontSize: 12 }} />
              </PieChart>
            </ResponsiveContainer>
          )}
        </ChartCard>
        <ChartCard title="Programme mix">
          {progMix.length === 0 ? <Empty /> : (
            <ResponsiveContainer width="100%" height={220}>
              <BarChart data={progMix} margin={{ top: 8, right: 8, left: -20, bottom: 0 }}>
                <CartesianGrid stroke="#1f1f1f" vertical={false} />
                <XAxis dataKey="name" stroke="#52525b" fontSize={11} tickLine={false} axisLine={{ stroke: "#1f1f1f" }} />
                <YAxis stroke="#52525b" fontSize={11} tickLine={false} axisLine={false} />
                <Tooltip contentStyle={{ background: "#0f0f0f", border: "1px solid #262626", borderRadius: 8, fontSize: 12 }} />
                <Bar dataKey="value" fill="#60a5fa" radius={[4, 4, 0, 0]} />
              </BarChart>
            </ResponsiveContainer>
          )}
        </ChartCard>
      </div>

      {/* Lead nurture */}
      <section>
        <div className="eyebrow mb-2">Lead nurture</div>
        <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
          <Kpi label="Scheduled" value={fmtN(bi.lead_nurture.scheduled)} icon={Users} tint="blue" />
          <Kpi label="Showed" value={fmtN(bi.lead_nurture.showed)} icon={Target} tint="emerald" />
          <Kpi label="Show %" value={pct(bi.lead_nurture.lead_show_pct)} icon={TrendingUp} tint="violet" />
          <Kpi label="Sold" value={fmtN(bi.lead_nurture.sold)} icon={IndianRupee} tint="emerald" />
          <Kpi label="Close %" value={pct(bi.lead_nurture.close_pct)} icon={TrendingUp} tint="amber" />
        </div>
      </section>

      {/* Rep leaderboard — visible to everyone (Feb 2026) */}
      {bi.by_rep?.length > 0 && (
        <section className="card-surface p-4 sm:p-6" data-testid="rep-leaderboard">
          <div className="eyebrow mb-3">Revenue by rep</div>
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="text-left text-[11px] uppercase tracking-[0.2em] text-zinc-500 border-b border-[#1f1f1f]">
                  <th className="px-3 py-2 font-medium">Rep</th>
                  <th className="px-3 py-2 font-medium text-right">Sales #</th>
                  <th className="px-3 py-2 font-medium text-right">Paid</th>
                  <th className="px-3 py-2 font-medium text-right">Net received</th>
                </tr>
              </thead>
              <tbody>
                {(bi.by_rep || []).map((r) => (
                  <tr key={r.name} className="border-b border-[#1a1a1a] last:border-0 hover:bg-[#161616]">
                    <td className="px-3 py-2.5 font-medium">{r.name}</td>
                    <td className="px-3 py-2.5 text-right">{r.count}</td>
                    <td className="px-3 py-2.5 text-right font-mono">{fmtINR(r.paid)}</td>
                    <td className="px-3 py-2.5 text-right font-mono text-emerald-400 font-semibold">{fmtINR(r.net)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </section>
      )}

      {/* Expenses by category */}
      <section className="card-surface p-4 sm:p-6" data-testid="expenses-by-category">
        <div className="eyebrow mb-3">Expenses by category</div>
        {Object.entries(kpis.expenses.by_category).every(([, v]) => v === 0) ? (
          <div className="text-sm text-zinc-500 py-6">No expenses logged for this month yet.</div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="text-left text-[11px] uppercase tracking-[0.2em] text-zinc-500 border-b border-[#1f1f1f]">
                  <th className="px-3 py-2 font-medium">Category</th>
                  <th className="px-3 py-2 font-medium text-right">Amount</th>
                  <th className="px-3 py-2 font-medium text-right">% of total</th>
                </tr>
              </thead>
              <tbody>
                {Object.entries(kpis.expenses.by_category)
                  .filter(([, v]) => v > 0)
                  .sort((a, b) => b[1] - a[1])
                  .map(([cat, amt]) => (
                    <tr key={cat} className="border-b border-[#1a1a1a] last:border-0 hover:bg-[#161616]">
                      <td className="px-3 py-2.5 font-medium">{cat}</td>
                      <td className="px-3 py-2.5 text-right font-mono text-rose-300">{fmtINR(amt)}</td>
                      <td className="px-3 py-2.5 text-right text-zinc-400 text-xs">
                        {kpis.expenses.total_expenses > 0 ? `${Math.round(amt/kpis.expenses.total_expenses * 1000)/10}%` : "—"}
                      </td>
                    </tr>
                  ))}
              </tbody>
            </table>
          </div>
        )}
      </section>

      {activeFacility?.offers_blood_tests !== false && <BloodTestsCard year={year} month={month} />}

      {/* Payment mode + Status mix */}
      {Object.keys(bi.payment_modes || {}).length > 0 && (
        <section className="card-surface p-4 sm:p-6">
          <div className="eyebrow mb-3">Payment mode split</div>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
            {Object.entries(bi.payment_modes).map(([mode, v]) => (
              <div key={mode} className="rounded-lg border border-[#1f1f1f] p-3 bg-[#0d0d0d]">
                <div className="text-[10px] uppercase tracking-[0.18em] text-zinc-500">{mode}</div>
                <div className="font-display text-xl font-extrabold mt-1 tabular-nums">{fmtINR(v.amount)}</div>
                <div className="text-[11px] text-zinc-500 mt-0.5">{v.count} transaction{v.count > 1 ? "s" : ""}</div>
              </div>
            ))}
          </div>
        </section>
      )}
      {statusMix.length > 0 && (
        <ChartCard title="Sale status mix">
          <ResponsiveContainer width="100%" height={220}>
            <BarChart data={statusMix} margin={{ top: 8, right: 8, left: -20, bottom: 0 }}>
              <CartesianGrid stroke="#1f1f1f" vertical={false} />
              <XAxis dataKey="name" stroke="#52525b" fontSize={11} tickLine={false} axisLine={{ stroke: "#1f1f1f" }} />
              <YAxis stroke="#52525b" fontSize={11} tickLine={false} axisLine={false} />
              <Tooltip contentStyle={{ background: "#0f0f0f", border: "1px solid #262626", borderRadius: 8, fontSize: 12 }} />
              <Bar dataKey="value" fill="#34d399" radius={[4, 4, 0, 0]} />
            </BarChart>
          </ResponsiveContainer>
        </ChartCard>
      )}
      </div>{/* end exportRef */}
    </div>
  );
}

function BigKpi({ label, value, sub, icon: Icon, tint, delta, invertDelta, format, testid }) {
  const tints = { emerald: "text-emerald-400", rose: "text-rose-400", blue: "text-blue-400", violet: "text-violet-400" };
  return (
    <div className="card-surface p-4 sm:p-6" data-testid={testid}>
      <div className="flex items-center justify-between">
        <div className="eyebrow">{label}</div>
        {Icon && <Icon className={`h-5 w-5 ${tints[tint] || "text-zinc-500"}`} strokeWidth={1.5} />}
      </div>
      <div className={`font-display text-2xl sm:text-3xl lg:text-4xl font-extrabold mt-3 tabular-nums ${tints[tint] || ""}`}>{value}</div>
      <div className="flex items-center gap-2 mt-2 flex-wrap">
        {sub && <div className="text-xs text-zinc-500">{sub}</div>}
        {delta && <Delta d={delta} invert={invertDelta} format={format} />}
      </div>
    </div>
  );
}

function Kpi({ label, value, sub, icon: Icon, tint, scopeBadge, testid }) {
  const tints = { emerald: "text-emerald-400", rose: "text-rose-400", blue: "text-blue-400", violet: "text-violet-400", amber: "text-amber-400" };
  return (
    <div className="card-surface p-4" data-testid={testid}>
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-1.5">
          <div className="text-[10px] uppercase tracking-[0.2em] text-zinc-500">{label}</div>
          {scopeBadge === "month" && (
            <span className="text-[9px] uppercase tracking-[0.15em] text-amber-400/70" title="This metric is monthly — it does not narrow to the selected week.">monthly</span>
          )}
        </div>
        {Icon && <Icon className={`h-3.5 w-3.5 ${tints[tint] || "text-zinc-500"}`} />}
      </div>
      <div className={`font-display text-2xl font-extrabold mt-2 tabular-nums ${tints[tint] || ""}`}>{value}</div>
      {sub && <div className="text-[11px] text-zinc-500 mt-1">{sub}</div>}
    </div>
  );
}

function Delta({ d, invert, format }) {
  if (!d) return null;
  const val = d.abs;
  const positive = invert ? val < 0 : val > 0;
  const negative = invert ? val > 0 : val < 0;
  const cls = positive ? "text-emerald-400" : negative ? "text-rose-400" : "text-zinc-500";
  const Icon = val > 0 ? ArrowUp : val < 0 ? ArrowDown : Minus;
  const pctStr = d.pct == null ? "" : ` (${d.pct > 0 ? "+" : ""}${Math.round(d.pct * 1000) / 10}%)`;
  const abs = format === "num" ? fmtN(val) : fmtINR(val);
  return (
    <span className={`inline-flex items-center gap-1 text-[11px] font-mono ${cls}`}>
      <Icon className="h-3 w-3" /> {val > 0 ? "+" : ""}{abs}{pctStr}
    </span>
  );
}

function ChartCard({ title, children }) {
  return (
    <div className="card-surface p-4 sm:p-6">
      <div className="eyebrow mb-3">{title}</div>
      {children}
    </div>
  );
}
function Empty() { return <div className="h-[220px] flex items-center justify-center text-sm text-zinc-500">No data this month</div>; }

function WeekChip({ active, onClick, label, sub, testid }) {
  return (
    <button
      onClick={onClick}
      data-testid={testid}
      className={`px-3 py-2 rounded-lg border text-left transition-colors ${
        active ? "bg-blue-500/10 border-blue-500/40 text-white" : "bg-[#0d0d0d] border-[#262626] text-zinc-400 hover:text-white hover:border-[#404040]"
      }`}
    >
      <div className="text-xs font-medium">{label}</div>
      {sub && <div className="text-[10px] text-zinc-500 mt-0.5">{sub}</div>}
    </button>
  );
}


function HeadlineKpiStrip({ year, month }) {
  const [data, setData] = useState(null);
  useEffect(() => {
    dashboardHeadlineKpis(year, month).then(setData).catch(() => setData(null));
  }, [year, month]);
  const d = data || {};
  const items = [
    { key: "onboarded", label: "New clients onboarded", value: d.new_clients_onboarded ?? 0, tint: "emerald" },
    { key: "fe-revenue", label: "FE revenue collected", value: `${window.__ACTIVE_CURRENCY_SYMBOL__ || ""}${fmtN(d.fe_revenue_collected ?? 0)}`, tint: "blue" },
    { key: "conversion", label: "Sales conversion", value: `${d.sales_conversion_pct ?? 0}%`, sub: `${d.sales_conversion_detail?.sold ?? 0} sold of ${d.sales_conversion_detail?.consults ?? 0}`, tint: (d.sales_conversion_pct ?? 0) >= 30 ? "emerald" : (d.sales_conversion_pct ?? 0) >= 15 ? "amber" : "rose" },
    { key: "acc-converted", label: "Accountability converted", value: `${d.accountability_converted ?? 0} / ${d.accountability_finishing ?? 0}`, sub: "finishing this month", tint: "violet" },
    { key: "acc-pct", label: "Accountability conversion", value: `${d.accountability_conversion_pct ?? 0}%`, tint: (d.accountability_conversion_pct ?? 0) >= 40 ? "emerald" : (d.accountability_conversion_pct ?? 0) >= 20 ? "amber" : "rose" },
    { key: "renewals", label: "Non-FE renewals", value: `${d.non_fe_renewed ?? 0} / ${d.non_fe_renewals_due ?? 0}`, sub: "renewed / due this month", tint: "blue" },
    { key: "exits", label: "Exits this month", value: d.exits_this_month ?? 0, tint: (d.exits_this_month ?? 0) === 0 ? "emerald" : "rose" },
    { key: "renewal-pending", label: "Lapsed renewals · unresolved", value: d.renewal_pending_now ?? 0, sub: "end date past · not exited", tint: (d.renewal_pending_now ?? 0) === 0 ? "emerald" : "amber" },
  ];
  const tints = { emerald: "text-emerald-300", amber: "text-amber-300", rose: "text-rose-300", blue: "text-blue-300", violet: "text-violet-300" };
  return (
    <section data-testid="headline-kpi-strip">
      <div className="eyebrow mb-3">Headline numbers · this month</div>
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        {items.map((it) => (
          <div key={it.key} className="card-surface p-4" data-testid={`headline-${it.key}`}>
            <div className="text-[10px] uppercase tracking-[0.22em] text-zinc-500">{it.label}</div>
            <div className={`font-display text-2xl font-bold tabular-nums mt-1.5 ${tints[it.tint] || "text-zinc-100"}`}>{it.value}</div>
            {it.sub && <div className="text-[11px] text-zinc-500 mt-1">{it.sub}</div>}
          </div>
        ))}
      </div>
    </section>
  );
}


function SoldNotStartedCard() {  const [data, setData] = useState({ count: 0, pending_incentive_base: 0, items: [] });
  const [open, setOpen] = useState(false);
  const fmtDate = (iso) => iso ? new Date(iso).toLocaleDateString(undefined, { day: "numeric", month: "short", year: "numeric" }) : "—";
  useEffect(() => {
    listSoldNotStarted().then(setData).catch(() => {});
  }, []);
  return (
    <section data-testid="sold-not-started-section">
      <div className="eyebrow mb-3">Pending payouts · Sold but programme not started</div>
      <button
        onClick={() => data.count > 0 && setOpen(true)}
        disabled={data.count === 0}
        className={`w-full text-left card-surface p-4 sm:p-6 group ${data.count > 0 ? "hover:border-amber-500/40 cursor-pointer transition-colors" : "cursor-default opacity-70"}`}
        data-testid="sold-not-started-card"
      >
        <div className="flex items-center gap-4">
          <div className="h-12 w-12 rounded-2xl bg-amber-500/10 border border-amber-500/30 flex items-center justify-center">
            <Timer className="h-5 w-5 text-amber-400" />
          </div>
          <div className="flex-1">
            <div className="text-[10px] uppercase tracking-[0.24em] text-zinc-500">Sold, not started</div>
            <div className="font-display text-4xl font-bold text-amber-300 tabular-nums" data-testid="sold-not-started-count">{data.count}</div>
            <div className="text-xs text-zinc-400 mt-1">Incentive is held until programme starts · pending base {window.__ACTIVE_CURRENCY_SYMBOL__ || ""}{new Intl.NumberFormat(numberLocale()).format(data.pending_incentive_base || 0)}</div>
          </div>
          {data.count > 0 && (
            <div className="text-[11px] text-amber-300/70 group-hover:text-amber-300 transition-colors">Click to view →</div>
          )}
        </div>
      </button>
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="bg-[#0f0f0f] border-[#262626] text-zinc-100 max-w-3xl" data-testid="sold-not-started-dialog">
          <DialogHeader>
            <DialogTitle className="font-display text-xl">Sold — programme not started</DialogTitle>
          </DialogHeader>
          <div className="mt-2 max-h-[65vh] overflow-y-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="text-left text-[10px] uppercase tracking-[0.2em] text-zinc-500 border-b border-[#1f1f1f]">
                  <th className="py-2 pr-3">Member</th>
                  <th className="py-2 pr-3">Sales Ex</th>
                  <th className="py-2 pr-3">Programme</th>
                  <th className="py-2 pr-3">Sold on</th>
                  <th className="py-2 pr-3 text-right">Paid</th>
                </tr>
              </thead>
              <tbody>
                {(data.items || []).map((r) => (
                  <tr key={r.id} className="border-b border-[#1a1a1a] hover:bg-[#161616]" data-testid={`sns-row-${r.id}`}>
                    <td className="py-2.5 pr-3">
                      <div className="font-medium text-zinc-100">{r.name || "—"}</div>
                      <div className="text-[11px] text-zinc-500">{r.phone || "—"}</div>
                    </td>
                    <td className="py-2.5 pr-3 text-zinc-300">{r.sales_ex || "—"}</td>
                    <td className="py-2.5 pr-3 text-zinc-300 text-xs">{r.programme || "—"}</td>
                    <td className="py-2.5 pr-3 text-zinc-400 text-xs whitespace-nowrap">{fmtDate(r.date)}</td>
                    <td className="py-2.5 pr-3 text-right font-mono text-emerald-300">{window.__ACTIVE_CURRENCY_SYMBOL__ || ""}{new Intl.NumberFormat(numberLocale()).format(r.amount_paid || 0)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </DialogContent>
      </Dialog>
    </section>
  );
}


// Blood tests sold at this gym in the month: each 6WC (or Annual) sold with blood tests is two tests,
// and what they cost us (the country's blood test cost per member, Global Admin → Incentives).
function BloodTestsCard({ year, month }) {
  const [d, setD] = useState(null);
  const [open, setOpen] = useState(false);
  useEffect(() => { setD(null); getBloodTests(year, month).then(setD).catch(() => setD(null)); }, [year, month]);
  if (!d) return null;
  const cur = d.currency_symbol || `${d.currency_code} `;
  const fmt = (n) => `${cur}${Math.round(n).toLocaleString(numberLocale())}`;
  return (
    <section className="card-surface p-4 sm:p-6" data-testid="blood-tests-card">
      <div className="flex items-start justify-between gap-3">
        <div>
          <div className="eyebrow">Blood tests sold</div>
          <div className="font-display text-2xl font-extrabold mt-1 tabular-nums">{d.members} member{d.members === 1 ? "" : "s"} · {d.tests} tests</div>
          <div className="text-xs text-zinc-500 mt-0.5">
            {d.cost_per_member ? `Cost to us ${fmt(d.cost)} (${fmt(d.cost_per_member)} a member)` : "Blood test cost not set in Global Admin"}
            {Object.keys(d.by_programme).length > 0 && ` · ${Object.entries(d.by_programme).map(([p, n]) => `${p} ${n}`).join(", ")}`}
          </div>
        </div>
        {d.members > 0 && <Button variant="outline" size="sm" className="h-8 border-[#262626] bg-transparent" onClick={() => setOpen((o) => !o)}>{open ? "Hide" : "Members"}</Button>}
      </div>
      {open && (
        <ul className="mt-3 divide-y divide-[#1a1a1a] text-sm">
          {d.items.map((it) => (
            <li key={it.id} className="py-1.5 flex flex-wrap justify-between gap-2">
              <span className="text-zinc-200">{it.member} <span className="text-zinc-500">· {it.programme}</span></span>
              <span className="text-xs text-zinc-500">{it.date}{it.rep ? ` · sold by ${it.rep}` : ""}</span>
            </li>
          ))}
        </ul>
      )}
    </section>
  );
}
